import { useEffect, useState } from 'react';

export interface HeaderState {
    /** True once the page has scrolled past the header's own height. */
    scrolled: boolean;
    /** True while scrolling down past the fold, so the bar can slide away. */
    hidden: boolean;
    /** data-theme of whichever section currently sits under the header. */
    themeUnder: 'dark' | 'light' | 'accent';
}

/**
 * Drives the header's appearance from the page beneath it.
 *
 * The header is transparent over the hero, frosts once you scroll, slides out
 * of the way while scrolling down, and — the part that matters — inverts its
 * colours to match the section passing underneath, so it stays legible over
 * both the black and white panels.
 *
 * Section detection samples the element under the header's lower edge rather
 * than observing every section, so it keeps working no matter how sections are
 * reordered or added.
 */
export function useHeaderState(): HeaderState {
    const [state, setState] = useState<HeaderState>({
        scrolled: false,
        hidden: false,
        themeUnder: 'dark',
    });

    useEffect(() => {
        let lastY = window.scrollY;
        let frame = 0;

        const read = () => {
            frame = 0;
            const y = window.scrollY;

            const headerH =
                parseFloat(
                    getComputedStyle(document.documentElement).getPropertyValue('--header-height')
                ) * 16 || 72;

            // Sample just below the header, at a point clear of the centred
            // content so it lands on the section itself.
            let themeUnder: HeaderState['themeUnder'] = 'dark';
            const probe = document.elementFromPoint(Math.min(24, window.innerWidth - 1), headerH + 4);
            const themed = probe?.closest<HTMLElement>('[data-theme]');
            const value = themed?.dataset.theme;
            if (value === 'light' || value === 'dark' || value === 'accent') themeUnder = value;

            setState((prev) => {
                const scrolled = y > headerH * 0.6;
                // Only hide well past the fold, and never while scrolling up.
                const hidden = y > headerH * 4 && y > lastY;
                lastY = y;
                if (
                    prev.scrolled === scrolled &&
                    prev.hidden === hidden &&
                    prev.themeUnder === themeUnder
                ) {
                    return prev;
                }
                return { scrolled, hidden, themeUnder };
            });
        };

        const onScroll = () => {
            if (!frame) frame = window.requestAnimationFrame(read);
        };

        read();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    return state;
}
